import { CustomVendorList, MediaSubtype } from '@accedo/one-plugin-sdk';
import { IconNames } from '@dynamic/ui';
import { ResourceKey } from 'i18next';

import { CommonAction } from '#/service/config';
import { ButtonVariant, FormFactors } from '#/service/constants';
import { MenuItemDisplayType } from '#/service/constants/menu';

export interface LoginRequest {
  deviceInfo: DeviceInfo;
  values: LoginRequestValues;
  secondScreen?: {
    code: string;
  };
}

export interface ChangePasswordCommand extends TFormData {
  currentPassword: string;
  newPassword: string;
  newPasswordConfirm: string;
}

export interface LoginRequestValues {
  signInEmailAddress: string;
  currentPassword: string;
}

export interface DeviceInfoRequest {
  deviceInfo: DeviceInfo;
}
export interface DeviceInfo {
  id: string;
  hardware: {
    manufacturer: string;
    model: string;
    version: string;
  };
  os: {
    name: string;
    version: string;
  };
  display: {
    width: number;
    height: number;
  };
  legal?: {
    tcfv2?: string;
    customVendors?: CustomVendorList;
  };
}

export type TFormData = { [key: string]: string | boolean | undefined };

export interface RegisterRequest {
  deviceInfo: DeviceInfo;
  values: TFormData;
}

export interface AuthResult {
  _meta: {
    auth: TokenResponse;
    type: string | FormSubmissionResTypes;
  };
}

export type AuthCodeRequestType = 'AUTHENTICATION' | 'REGISTRATION';
export enum AuthCodeStatus {
  Pending = 'PENDING_SECOND_SCREEN_ACTION',
  Canceled = 'CANCELED',
  Expired = 'EXPIRED',
  Completed = 'FLOW_COMPLETED',
}

export interface AuthCodeCheckResponse {
  codeEntry: AuthCodeEntry;
  /** Contains parts of the deviceInfo structure from the primary device. This may be used by the second screen to display hints to the user like "To login on your *Roku* device, enter your credentials here" */
  primaryDeviceInfo?: {
    manufacturer: string;
    operatingSystem: string;
  };
  status: AuthCodeStatus;
}

interface AuthCodeEntry {
  /** The authentication code used for the Second Screen Authentication flow */
  code: string;

  /** When authentication code expires */
  expiresAt: string;

  /** The type of flow the primary device wants to trigger. Only login is supported at this time */
  requestType: AuthCodeRequestType;
}

export enum AuthenticationType {
  None = 'none',
  Standard = 'standard',
  TVE = 'tve',
  Anonymous = 'anonymous',
}

export interface TokenResponse {
  token: string;
  refreshToken: string;
  expiration: string;
  principal?: {
    userId: string;
  };
}

export interface OauthTokenResponse {
  oauth?: {
    accessToken?: string;
  };
}

export interface ProfileResponse {
  _meta: MetaResponse;
  userId: string | undefined;
  authentication: Authentication;
  entitlements: Entitlements;
  profile: Profile;
  subscriptions?: Subscription[];
  subscriber?: boolean;
}

// Preference types
// - these types are added just for starters according to preferences feature
// - these should be handled as form input definitions types later
type PreferenceType = 'static' | 'input';
type PreferenceInputVariant = 'checkbox' | 'multi_select' | 'select';
type PreferenceStaticVariant = 'heading' | 'image' | 'checkbox';
type SelectDisposition = 'GRID' | 'DROPDOWN';

export interface PreferencesResponse {
  _meta: MetaResponse;
  display?: PreferenceDisplay[];
}

// only added multi-select as we are only using this type yet
type PreferenceDisplay = MultiSelectDisplay;

interface PreferenceDisplayBase {
  type: PreferenceType;
  variant: PreferenceInputVariant | PreferenceStaticVariant;
}

export interface MultiSelectDisplay extends PreferenceDisplayBase {
  key: string;
  label: string;
  description: string;
  displayConfiguration?: {
    disposition?: SelectDisposition;
  };
  required: boolean;
  values: MultiSelectOption[];
}

export interface MultiSelectOption {
  key: string;
  label: string;
  imageUrl?: string;
  color?: string;
  value: any;
}

export interface MetaResponse {
  type?: string;
  errors?: object;
}

export interface Profile {
  attributes: {
    [key: string]: any;
    mvpdId?: string;
  };
  preferences?: Preferences;
}

export interface Preferences {
  [key: string]: any;
}

export interface Entitlements {
  subscriber: boolean;
  canDownloadVideos?: boolean;
  subscriptions?: Subscription[];
  grantedEntitlementTags?: string[];
}

type PurchaseMethod =
  | 'CREDIT_CARD'
  | 'UNKNOWN'
  | 'IAP_AMAZON'
  | 'IAP_APPLE'
  | 'IAP_GOOGLE'
  | 'IAP_ROKU';

export interface Subscription {
  id: string;
  label: string;
  purchaseMethod: PurchaseMethod;
  appleProductDetails?: any;
}

export interface Authentication {
  type: AuthenticationType;
  expiration: string;
}

export interface FavoritesResponse {
  data: FavoriteItem[];
}

// `video.watched` object in item and history responses
export interface Watched {
  date?: string;
  progress?: {
    percentage: number;
    seconds: number;
    finished: boolean;
  };
}

// one item in the `watch-history/all` response
export interface HistoryAllItem {
  objectId: string;
  objectType: MediaType;
  watched?: Watched;
}

// `watch-history/all` response
export interface HistoryAllResponse {
  data: HistoryAllItem[];
}

export interface FavoriteItem {
  objectId: string;
  objectType: MediaType;
}

export interface Locale {
  endonym: string;
  exonym: string;
  code: string;
  fallback?: boolean;
}

export interface TranslationResponse {
  translations: ResourceKey;
}
export interface LocalesResponse {
  locales: Locale[];
}

export interface InputFeedResponse {
  display: InputApiField[];
  _meta?: {
    type: string;
  };
}

export type TFormResponse = {
  _meta: {
    auth?: TokenResponse;
    type?: FormSubmissionResTypes;
    message?: string;
    error?: {
      validation?: { [key: string]: string[] };
    };
    ignoreError?: boolean; // backend response will never include this, but we need it for `CommonForm`
  };
  originalError?: Error;
};

export type DisplayConfiguration = {
  multiline?: boolean;
  disposition?: 'DROPDOWN' | 'GRID';
};

export interface InputApiField {
  key: string;
  label?: string;
  placeholder?: string;
  description?: string;
  required?: boolean;
  variant?: string;
  linkPath?: string;
  displayConfiguration?: DisplayConfiguration;
  heading?: {
    text: string;
  };
  type: FormInputTypes.INPUT | FormInputTypes.STATIC;
}

export interface ChangeFavoriteRequest {
  id: string;
  type: MediaType;
}

export enum ResourceType {
  Item = 'item',
  Collection = 'collection',
}

type Video = {
  duration?: number | string;
  playback: string;
  accessLevel?: AccessLevel;
  watched?: Watched;
  closedCaptions?: {
    availableLanguages: string[];
  };
  entitlementTags?: string[];
  markers?: {
    fullyWatched: number;
  };
  parentalRatings?: ParentalRating[];
  viewableOffline?: boolean;
};

interface ParentalRating {
  type: string;
  value: string;
}

export enum MediaType {
  Item = 'ITEM',
  Collection = 'COLLECTION',
}

export interface Item extends MediaResource {
  video: Video;
}

export interface ResourceData {
  collection?: Collection;
  data: Item[] | Item;
  pageInfo?: PageInfo;
}

export type FeedResponse = {
  feed?: string;
  data: Item[];
  error?: Error;
  pageInfo: PageInfo;
};

export type PageInfo = {
  total: number;
  hasMore: boolean;
  page: number;
  pageSize: number;
};

interface ImageVariants {
  wide?: ImageDetails[];
  '16:9'?: ImageDetails[];
  portrait?: ImageDetails[];
  landscape?: ImageDetails[];
  square?: ImageDetails[];
}

export interface MediaResource {
  id: string;
  title: string;
  description?: string;
  extras?: object;
  shortDescription?: string;
  releaseDate?: string;
  genres?: string[];
  playback?: string;
  subtype: MediaSubtype;
  refs: ItemRefs;
  images: {
    poster?: ImageVariants;
    still?: ImageVariants;
    background?: ImageVariants;
    logo?: ImageVariants;
    thumbnail?: ImageVariants;
  };
  type: MediaType;
  episode?: {
    number: number;
  };
  tvShow?: {
    id: string;
    title: string;
  };
  tvShowSeason?: {
    id: string;
    season: {
      number: number;
      title?: string;
    };
    title?: string;
  };
  show?: {
    seasonCount: number;
  };
  upNext?: Item;
  availability?: {
    from: string;
    fromEpoch: number;
    until: string;
    untilEpoch: number;
  };
  cast?: any;
  releaseDateEpoch?: number;
}

export interface ItemRefs {
  self: string;
  next: string;
  download?: string;
  share?: string;
  tvShow?: string;
  tvShowSeason?: string;
}

export interface ResourceItem {
  data: Item;
}

export interface SeasonCollection extends MediaResource {
  season?: {
    episodeCount: number;
    number: number;
  };
}

export interface Collection {
  collection: MediaResource;
  data: SeasonCollection[];
}

interface ImageDetails {
  url: string;
  label: string;
  sizes?: ImageSize[];
  blurhash?: string;
}

interface ImageSize {
  width: number;
  height: number;
}

export enum AccessLevel {
  UNRESTRICTED = 'UNRESTRICTED',
  AUTHENTICATION_REQUIRED = 'AUTHENTICATION_REQUIRED',
  ENTITLEMENT_REQUIRED = 'ENTITLEMENT_REQUIRED',
}
interface ItemTemplateRule {
  template: {
    component: {
      properties: {
        action?: {
          params: {
            destination: {
              id: string;
            };
          };
        };
      };
    };
  };
}

export interface ContainerData {
  id: string;
  data: {
    collectionId?: string;
    feed: string;
    originalFeed?: string;
    initial: Item[] | undefined;
    pageInfo?: PageInfo;
  };
  itemTemplateRules: ItemTemplateRule[];
  templates?: {
    [key: string]: {
      id: string;
    };
  };
  visibility?: Condition;
}

export type PageLayout = {
  page: LayoutData;
  templates: V2TemplatesData;
};

export interface ContainersData {
  [key: string]: Item[];
}

export interface CollectionData {
  componentId: string;
  containers: ContainerData[];
}

export interface TemplateProps {
  navigationBar: '[]';
  title?: string;
  topContainerShouldNotOverlap?: boolean;
}

export interface LayoutData {
  id: string;
  containerCollections: CollectionData[];
  template?: {};
  templates?: {
    [key: string]: {
      id?: string;
      properties: TemplateProps;
    };
  };
}

export interface ContainerChildren {
  id: string;
  type: string;
}

export interface V2TemplatesData {
  pages: {};
  containers: {
    [key: string]: {
      component: {
        children: ContainerChildren[];
      };
    };
  };
  items: {};
}

export enum FormSubmissionResTypes {
  ERROR = 'error',
  SUCCESS = 'success',
}

export enum FormInputTypes {
  INPUT = 'input',
  PLAN = 'plan',
  STATIC = 'static',
}

export interface TransformedChannel extends Item {
  logo?: string;
}

export interface ChannelResponse {
  data: Item[];
  pageInfo: PageInfo;
}

type Schedule = {
  startsAtEpoch?: number;
  endsAtEpoch?: number;
  startsAt?: string;
  endsAt?: string;
};

export interface ScheduleType extends Item {
  schedule?: Schedule;
}

export interface TransformedSchedule extends ScheduleType {
  subTitle: string;
}

export interface ScheduleResponse {
  channel: Item;
  data: ScheduleType[];
  pageInfo: PageInfo;
}

export interface MvpdResponse {
  mvpds: Mvpd[];
}

export interface Mvpd {
  id: string;
  name: string;
  logoUrl: string;
  promoted: boolean;
}

interface DeviceNetworkInfoBase {
  deviceInfo: DeviceInfo;

  networkInfo: {
    type: 'WIFI' | 'CELLULAR';
  };
}

export interface MvpdAuthRequest
  extends DeviceNetworkInfoBase,
    AuthRefreshRequest {
  /**
   * MVPD identifier. Not providing one will result in only checking state of authentication of the user. Providing one will kickoff authorization process.
   */
  mvpdId?: string;

  /** Used when second screen authorization is used */
  authorizationCode?: string;
}

export interface MvpdAuthResponse {
  _meta: {
    type: 'success' | 'error' | 'redirect' | 'second-screen';
    redirect?: {
      targetUrl: string;
      authorizationCode: string;
    };
    secondScreen?: {
      portalUrl: string;
      authorizationCode: string;
    };
    auth?: TokenResponse;
  };
}

export interface AuthRefreshRequest {
  secondScreen?: {
    code: string;
  };
}

export interface PurchaseProductsResponse {
  productIds: string[];
}

export interface GiftProductsResponse {
  productIds: string[];
}

/**
 * Generic type representing validation returned by the backend.
 * Note: for some endpoint the backend may return just an empty 200 OK
 * response when validation is successful.
 * */
export interface ValidationResponse {
  _meta: {
    type?: string;
    message?: string;
    error?: object;
  };
}

export type CoreMenuConfig = {
  [FormFactors.DESKTOP]?: FormFactorMenuConfig;
  [FormFactors.MOBILE]?: FormFactorMenuConfig;
  [FormFactors.TABLET]?: FormFactorMenuConfig;
  [FormFactors.TV]?: FormFactorMenuConfig;
};

interface FormFactorMenuConfig {
  menus: MenuForFormFactor[];
}

interface MenuForFormFactor {
  id: string;
  items: MenuItem[];
}

export interface MenuItem {
  id: string;
  display: MenuItemDisplay;
  action: CommonAction;
  visibility?: Condition;
}

interface MenuItemDisplay {
  type: MenuItemDisplayType;
  title?: string;
  icon?: IconNames;
  buttonVariant?: ButtonVariant;
}

export type Condition = {
  type: string;
  subject: string;
  value: any;
  negate?: boolean;
  conditions?: Condition[];
};

export interface ActionParams {
  destination: DestinationActionParams;
}

export interface DestinationActionParams {
  id: string;
  type: string;
}
